import navItems from "@/navigation/vertical";
import { usePosSetupStore } from "@/store/posSetupStore";
import { useUserModeChange } from "@/store/useUserModeChange";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { pb, RolesRecord } from "@/utils/PocketBaseAdapter";
import { usePermission } from "@/utils/usePermission";
import isEmpty from "lodash/isEmpty";
import { defineStore } from "pinia";
import { useUserStore } from "./UserStore";

export const useUserRole = defineStore("useUserRoles", () => {
  // Interface
  interface permissionMap {
    [key: string]: string[];
  }

  // store
  const usePermissions = usePermission();
  const userMode = useUserModeChange();
  const globalSetupStore = useGlobalSetupStore();
  const { globalSetup } = storeToRefs(globalSetupStore);
  const { isUserMode } = storeToRefs(userMode);
  const userStore = useUserStore();
  const posSetupStore = usePosSetupStore();
  const { currentLocationDetails, hasPrepArea } = storeToRefs(userStore);
  const isInitialized = ref(false);

  // Composable
  const { isHidden } = usePermissions;

  // Data
  const permissionListMap = ref<permissionMap>({});

  const { currentLocation } = storeToRefs(userStore);

  const rolesList = ref<any[]>([]);
  const rolesMap = ref<any>(new Map<String, RolesRecord>());
  const userInfo = ref<any>({});
  const accessReadersList = ref<any[]>([]);
  const getAccessReader = ref<any>([]);
  const getAccessReaderName = ref("");
  const barCodeReadersList = ref<any[]>([]);
  const barCodeReader = ref("");
  const userId = ref(pb?.authStore?.model?.id || null);
  const hasMatrixDevicesRecords = ref(false);
  const hasDoorsRecords = ref(false);

  const doorBarCodeReader = ref(
    localStorage.getItem("doorbarcodeReader") || ""
  );

  // Define the apps object
  const apps = ref<any>({
    POS: {
      isVisible: false,
      POSName: "",
      accessReader: [],
      terminal: "",
      barcodeReader: "",
    },
    Dockets: {
      isVisible: false,
    },
    Prep: {
      isVisible: false,
    },
    Kiosk: {
      isVisible: false,
    },
    "Staff TimeClock": {
      isVisible: false,
    },
  });

  // Function to initialize the apps ref with data from localStorage
  const initializeApps = async() => {
    try {
      // Get data from localStorage
      const appsJson = localStorage.getItem("Apps");
      
      if(appsJson) {
        apps.value = JSON.parse(appsJson);
      }

      const posSetup = await posSetupStore.load();

      // If data exists, parse it and update the ref
      if (posSetup) {
        const reader = accessReadersList.value?.find((reader) => reader.id === posSetup.accessReader);
        barCodeReader.value = posSetup.barCodeReader;

        if (reader) {
          getAccessReaderName.value = `<span class="fob-badge">${reader.name}</span>`;
          getAccessReader.value = reader;
        }
      }
    } catch (error) {
      console.error("Error initializing apps from localStorage: ", error);
    }
  };

  const isUserLoggedIn = computed(() => !!userId.value);

  /**
   * Computes and returns the name of the barcode reader associated with the POS app.
   *
   * @remarks
   * This function uses the `barCodeReadersList` and `apps` reactive refs to find the name of the barcode reader
   * associated with the POS app based on their IDs. If a matching reader is found, its name is returned.
   * If no matching reader is found or if either of the reactive refs is not defined, an empty string is returned.
   *
   * @returns {string} - The name of the barcode reader associated with the POS app, or an empty string if no matching reader is found.
   */
  const getBarcodeReaderName = computed(() => {
    return (
      barCodeReadersList.value?.find(
        (reader: any) => reader?.id === barCodeReader.value
      )?.name || ""
    );
  });

  /**
   * Computes and aggregates user permissions based on their roles and location.
   *
   * @remarks
   * This function filters relevant roles based on the user's roles and location,
   * then aggregates permissions from these roles. It resolves conflicts by prioritizing
   * permissions based on a predefined order.
   *
   * @returns {Object} - An object representing aggregated permissions, where keys are permission keys
   * and values are either a single permission level or an array of permission levels.
   */
  const permission = computed(() => {
    const permissionPriority = ["Create", "Edit", "Assign", "View", "None"];
    const relevantRoles = !isUserMode.value
      ? rolesList.value?.filter(
          (role) =>
            userInfo.value?.roles?.includes(role.id) &&
            role.location === currentLocation.value
        )
      : [];
    const aggregatedPermissions = relevantRoles?.reduce((acc, role) => {
      Object.keys(role).forEach((permissionKey) => {
        if (!acc[permissionKey]) {
          acc[permissionKey] = new Set();
        }
        acc[permissionKey].add(role[permissionKey]);
      });
      return acc;
    }, {});

    Object.keys(aggregatedPermissions)?.forEach((permissionKey) => {
      const permissionsMap = Array.from(aggregatedPermissions[permissionKey]);
      if (permissionsMap.some((el: any) => permissionPriority?.includes(el))) {
        aggregatedPermissions[permissionKey] =
          permissionPriority?.find((permission) =>
            permissionsMap?.includes(permission)
          ) || "None";
      } else {
        aggregatedPermissions[permissionKey] = permissionsMap;
      }
    });

    return aggregatedPermissions;
  });

  /**
   * Computed property to generate the list of navigation items based on user permissions.
   *
   * @returns {Array} - An array of navigation items with their 'disable' property set based on user permissions.
   */
  const navListItems = computed(() => {
    if (!isInitialized.value) {
      initialize();
      return [];
    }
    // Determine if the "Apps" heading should be visible based on the visibility of its sub-links
    const isAppsHeadingVisible = Object.values(apps.value).some(
      (app: any) => app?.isVisible
    );

    // Cached app visibility for use within the reduce function
    const appNavItems: any = {
      Dockets: true,
      POS: true,
      Kiosk: apps.value.Kiosk?.isVisible,
      Prep: hasPrepArea.value && currentLocationDetails.value?.usesPrepScreens,
      "Staff TimeClock": apps.value["Staff TimeClock"]?.isVisible,
    };

    let hasWebShopAdded = false;

    const resolvedItems = navItems?.filter((el:any) => !el?.hide).reduce((acc: any[], el: any) => {
      const disable = el.disable || false;

      // Hide nav items where the role doesn't meet the required permission tier
      if (isHidden(el, permission.value)) return acc;

      // Specific check for "WebShop" visibility
      if (el.title === "WebShop") {
        // Check if either `webShopEnabled` or `onlineCreditCardsEnabled` is false
        if (
          !currentLocationDetails.value?.webshopEnabled ||
          !currentLocationDetails.value?.onlineCreditCardsEnabled
        ) {

          return acc; // Skip adding WebShop
        }
      
        // If both are true, check if `onlineShopping` is false
        if (
          !currentLocationDetails.value?.onlineShopping &&
          !hasWebShopAdded
        ) {
          // acc.push({ ...el, disable: true }); // Don't show if disabled
          hasWebShopAdded = true;
          return acc;
        }
      }
      

      // Check if user is not logged in
      if (!isUserLoggedIn.value) {
        // Only include items where isAuthRequired is false
        if (el.isAuthRequired === false) {
          acc.push({ ...el, disable });
        }
        return acc; // Skip further checks if not logged in
      }

      // Skip items where isAuthRequired is false if user is logged in
      if (isUserLoggedIn.value && el.isAuthRequired === false) {
        return acc;
      }

      // Handle "Apps" heading visibility
      if (el.heading === "Apps" && isUserMode.value) {
        if (isAppsHeadingVisible) acc.push({ ...el, disable });
        return acc;
      }

      // Check permissions if required
      const hasPermission = !el.permissionRequired || permission.value?.[el.permissionRequired]?.includes(true);

      if (
        (el.title === "Matrix Displays" || el.title === "Advertising") &&
        !hasMatrixDevicesRecords.value
      ) {
        return acc;
      }

      if (el.title === "Access Control" && !hasDoorsRecords.value) {
        return acc;
      }
      
      if (hasPermission && (!el.isSetToVisible || appNavItems[el.title])) {
        const areaPlural = globalSetup.value?.areaPlural;
        const areaSingular = globalSetup.value?.areaSingular;
        let title = el.title;
        if (el.title === "Areas") {
          title = areaPlural;
        } else if (el.title === "Area Status") {
          title = `${areaSingular} Status`;
        }
        acc.push({ ...el, title, disable });
      }

      return acc;
    }, []);

    // Drop any heading that has no items following it (i.e. everything
    // under it got filtered out above) before the next heading or the end
    // of the list.
    return resolvedItems.reduce((cleaned: any[], item: any, idx: number) => {
      if (item.heading) {
        const nextItem = resolvedItems[idx + 1];
        if (!nextItem || nextItem.heading) return cleaned;
      }
      cleaned.push(item);
      return cleaned;
    }, []);
  });

  /**
   * Generates a permission list based on the user's role and location.
   *
   * @remarks
   * This function is responsible for transforming the user's role permissions into a more detailed and usable format.
   * It iterates through the user's role permissions, applies a predefined set of permissions for each role permission,
   * and stores the result in the `permissionListMap` reactive ref.
   *
   * @returns {void}
   */
  const generatePermission = (): void => {
    const getPermissionsForRole = (currentPermission: any) => {
      switch (currentPermission) {
        case "Create":
          return ["Create", "Edit", "View"];
        case "Edit":
          return ["Edit", "View", "None"];
        case "Assign":
          return ["Assign", "View", "None"];
        case "View":
          return ["View", "None"];
        default:
          return ["None"];
      }
    };

    if (!isEmpty(permission.value)) {
      const permissionList: permissionMap = {};
      for (let per in permission.value) {
        permissionList[per] = getPermissionsForRole(permission.value[per]);
      }
      permissionListMap.value = permissionList;
    } else {
      permissionListMap.value = {};
    }
  };

  const getUserRolePermission = async (inList?: boolean) => {
    try {
      if (!rolesList.value.length || inList) {
        const recordList = await pb
          .collection("roles")
          .getFullList<RolesRecord>();
        rolesList.value = recordList;
        recordList.forEach((role: any) => rolesMap.value.set(role.id, role));
      }

      if (isEmpty(userInfo.value)) {
        const userId = pb.authStore.model?.id;

        const record = await pb
          .collection("users")
          .getOne(userId, { requestKey: "userprofile", expand: "membership" });
        userInfo.value = record;
      }
    } catch (e) {
      console.error(e)
    }
  };

  const getAccessReaderList = async () => {
    try {
      const accessReaderList = await pb.collection("accessReaders").getFullList({
        expand: "door,lastFOBNum",
      });

      accessReadersList.value = accessReaderList;
    } catch (e) {}
  };

  const getBarCodeReaderList = async () => {
    try {
      const readerList = await pb.collection("barcodeReaders").getFullList();
      barCodeReadersList.value = readerList;
    } catch (e) {}
  };

  const getCollectionHasRecords = async (collectionName: string) => {
    try {
      const result = await pb.collection(collectionName).getList(1, 1, {
        fields: "id",
      });
      return result.totalItems > 0;
    } catch (e) {
      return false;
    }
  };

  // reset rolesList and rolesMap to clear permission on logout
  const $reset = () => {
    rolesList.value = [];
    userInfo.value = {};
    rolesMap.value.clear();
    permissionListMap.value = {};
    isInitialized.value = false;
  };

  const initialize = async () => {
  if (isInitialized.value) return; // Prevent multiple initializations

  try {
    await getUserRolePermission();
    await getAccessReaderList();
    await getBarCodeReaderList();
    hasMatrixDevicesRecords.value = await getCollectionHasRecords("matrixDevices");
    hasDoorsRecords.value = await getCollectionHasRecords("doors");
    
    await initializeApps();
  } catch (error) {
    console.error('Error in initialization:', error);
  } finally {
    isInitialized.value = true;
  }
};

// Temporary calls to be removed
  const temporaryCalls = async () => {
    await getUserRolePermission();
    initializeApps();
  };

  watchEffect(() => {
    if (permission.value) {
      generatePermission();
    }
  });

  // Run immediately on load to sync with initial value
  watch(
    () => pb.authStore?.model?.id,
    (newId) => {
      if (newId) {
        userId.value = newId;
      }
    },
    { immediate: true, deep: true }
  );

  return {
    doorBarCodeReader,
    permission,
    permissionListMap,
    navListItems,
    userInfo,
    currentLocation,
    rolesList,
    rolesMap,
    apps,
    accessReadersList,
    barCodeReadersList,
    getAccessReaderName,
    getAccessReader,
    getBarcodeReaderName,
    userId,
    getBarCodeReaderList,
    $reset,
    generatePermission,
    getUserRolePermission,
    getAccessReaderList,
    temporaryCalls
  };
});
